import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp } from 'lucide-react';

interface TooltipPosition {
  top: number;
  left: number;
  width: number;
}

interface PermissionHelpTooltipProps {
  ariaLabel: string;
  content: string;
  contentDataQa?: string;
  enabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tooltipKey: string;
  triggerDataQa?: string;
  width: number;
}

const viewportOffset = 8;
const tooltipZIndex = 99999994;

const PermissionHelpTooltip: React.FC<PermissionHelpTooltipProps> = ({
  ariaLabel,
  content,
  contentDataQa,
  enabled = true,
  open,
  onOpenChange,
  tooltipKey,
  triggerDataQa,
  width,
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !contentRef.current || typeof window === 'undefined') {
      return;
    }

    const tooltipWidth = Math.min(width, Math.max(window.innerWidth - viewportOffset * 2, 0));
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipHeight = contentRef.current.offsetHeight;
    const preferredLeft = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
    const maxLeft = Math.max(viewportOffset, window.innerWidth - tooltipWidth - viewportOffset);
    const left = Math.min(Math.max(preferredLeft, viewportOffset), maxLeft);
    const availableBelow = window.innerHeight - triggerRect.bottom - viewportOffset;
    const top =
      availableBelow >= tooltipHeight
        ? triggerRect.bottom + viewportOffset
        : Math.max(viewportOffset, triggerRect.top - tooltipHeight - viewportOffset);

    setPosition({
      top,
      left,
      width: tooltipWidth,
    });
  }, [width]);

  useLayoutEffect(() => {
    if (!enabled || !open) {
      setPosition(null);
      return;
    }

    updatePosition();

    const frameId = window.requestAnimationFrame(() => {
      updatePosition();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [enabled, open, updatePosition]);

  useEffect(() => {
    if (!enabled || !open) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        onOpenChange(false);
        return;
      }

      if (triggerRef.current?.contains(target) || contentRef.current?.contains(target)) {
        return;
      }

      onOpenChange(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [enabled, open, onOpenChange]);

  useEffect(() => {
    if (!enabled || !open) {
      return;
    }

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [enabled, open, updatePosition]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded text-[#64748B] hover:bg-[#E2E8F0]"
        onClick={() => {
          if (!enabled) {
            return;
          }
          onOpenChange(!open);
        }}
        aria-label={ariaLabel}
        data-permission-tooltip-trigger={tooltipKey}
        data-qa={triggerDataQa}
      >
        <CircleHelp className="h-4 w-4" />
      </button>

      {enabled && open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={contentRef}
              className="fixed rounded-[8px] border border-[#D8E0E8] bg-white p-3 text-[13px] leading-[1.4] text-[#475569] shadow-md"
              style={{
                top: `${position?.top ?? 0}px`,
                left: `${position?.left ?? 0}px`,
                width: `${position?.width ?? width}px`,
                visibility: position ? 'visible' : 'hidden',
                zIndex: tooltipZIndex,
              }}
              data-permission-tooltip-content={tooltipKey}
              data-qa={contentDataQa}
            >
              {content}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default PermissionHelpTooltip;
